import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import type { ReactElement, ReactNode, RefObject } from "react";
import { useRef } from "react";
import { Button, Card, Tabs, TabsContent, TabsList, TabsTrigger, cn } from "@ndd/ui";
import { rdFirstName } from "../../../../shared/starter-prompts";
import {
  appointmentViewTabs,
  formatAppointmentTimeRange,
  headerDateLabel,
  timeOfDayGreeting,
  type AppointmentListSearch,
  type AppointmentView,
  type UpNextAppointment,
} from "@/lib/appointments";
import type { AppointmentAgenda as AppointmentAgendaState } from "@/lib/use-appointment-agenda";
import {
  AGENDA_EYEBROW,
  AGENDA_PRIMARY_FILL,
  AGENDA_TAB_INK,
  AGENDA_UTILITY_RADIUS,
  PAGE_TITLE,
  QUIET_LINK,
} from "./agenda-look";
import { AppointmentSchedule } from "./appointment-schedule";
import { WeekHeaderActions, type AppointmentWeekSlot } from "./week-header-actions";

type AppointmentAgendaReady = Extract<AppointmentAgendaState, { status: "ready" }>;

export type { AppointmentView, AppointmentWeekSlot };

/** What the header shows; in Past it may come from the Past answer instead. */
export interface AppointmentAgendaHeader {
  display: { now: string; timezone: string };
  /** Null hides the count: unknown, or not proven complete. */
  todayCount: number | null;
  healthieCalendarUrl: string | null;
}

export interface AppointmentAgendaProps {
  view: AppointmentView;
  /** The switch pushes a history entry, so Back returns to the other view. */
  onViewChange: (view: AppointmentView) => void;
  header: AppointmentAgendaHeader;
  /** The Upcoming list; null only in Past while that list is not ready. */
  agenda: AppointmentAgendaReady | null;
  /**
   * The page h1: the RD's own greeting (`GreetingHeading`), or the scope
   * selector for a viewer who can name an RD. Whose schedule this is, and how
   * to change it, live in the title — never in the shell chrome.
   */
  heading: ReactNode;
  /** Another RD's schedule: copy must not address the viewer as its owner. */
  viewingOther: boolean;
  listSearch: AppointmentListSearch;
  showNonDefaultStatuses: boolean;
  onStatusChange: (checked: boolean) => void;
  onLoadMore: () => void;
  /** The Past view's section, rendered only while Past is selected. */
  past: ReactNode;
  /**
   * NO-878: the Availability panel. Null leaves the tab out; the route passes
   * it only while the Availability gate is open.
   */
  availability?: ReactNode;
  pastBusy: boolean;
  /** F7: the one live region's text, already chosen for the view. */
  liveText: string;
  /**
   * NO-874: the Week view, behind rd-schedule in the single-rail frame. Null
   * keeps the classic switch (Upcoming, Past), the Healthie link and no
   * header buttons.
   */
  week?: AppointmentWeekSlot | null;
}

/** Every view uses the Week view's 1200px so the header and tabs stay put when switching tabs. */
const AGENDA_WIDTH = "max-w-[1200px]";

export function AppointmentAgenda(props: AppointmentAgendaProps): ReactElement {
  const { view, onViewChange, header, agenda, heading, listSearch, past } = props;
  const pastTabRef = useRef<HTMLButtonElement | null>(null);

  // Focusing the Past tab selects it (automatic activation), so the pill moves
  // focus there and lets the switch make its one navigation.
  const viewPast = (): void => {
    const pastTab = pastTabRef.current;
    if (pastTab) pastTab.focus();
    else onViewChange("past");
  };

  const week = props.week ?? null;

  return (
    <div className={cn("@container mx-auto px-6 pb-12 pt-8", AGENDA_WIDTH)}>
      <Tabs
        value={view}
        onValueChange={(value) => onViewChange(value as AppointmentView)}
      >
        <AgendaHeader
          header={header}
          heading={heading}
          pastTabRef={pastTabRef}
          showAvailability={props.availability !== undefined}
          week={week}
        />

        {view === "upcoming" && agenda?.upNext ? (
          <UpNextCard
            upNext={agenda.upNext}
            timezone={agenda.display.timezone}
            listSearch={listSearch}
          />
        ) : null}

        <TabsContent value="upcoming" className="mt-0">
          {agenda ? (
            <UpcomingPanel {...props} agenda={agenda} onViewPast={viewPast} />
          ) : null}
        </TabsContent>
        {week ? (
          <TabsContent value="week" className="mt-0" aria-busy={week.busy}>
            {week.panel}
          </TabsContent>
        ) : null}
        <TabsContent value="past" className="mt-0" aria-busy={props.pastBusy}>
          {past}
        </TabsContent>
        {props.availability !== undefined ? (
          <TabsContent value="availability" className="mt-0">
            {props.availability}
          </TabsContent>
        ) : null}
      </Tabs>
      {week?.dialogs ?? null}
      {/* Outside the aria-busy panels — busy contents are ignored by AT,
          which would suppress this region's own announcements. */}
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {props.liveText}
      </span>
    </div>
  );
}

function AgendaHeader({
  header,
  heading,
  pastTabRef,
  showAvailability,
  week,
}: {
  header: AppointmentAgendaHeader;
  heading: ReactNode;
  pastTabRef: RefObject<HTMLButtonElement | null>;
  showAvailability: boolean;
  week: AppointmentWeekSlot | null;
}): ReactElement {
  // DOM order is the keyboard order: h1, Healthie link, then the switch. At a
  // narrow width the rows stack h1, date line, link, switch. With the Week
  // view on (NO-874) the buttons take the Healthie link's place: Week
  // replaces that link.
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 @max-[560px]:grid-cols-[minmax(0,1fr)]">
      <div className="col-start-1 row-start-1 min-w-0">{heading}</div>
      {week ? <WeekHeaderActions week={week} /> : null}
      {!week && header.healthieCalendarUrl ? (
        <a
          href={header.healthieCalendarUrl}
          target="_blank"
          rel="noreferrer"
          className={cn(
            "col-start-2 row-start-1 inline-flex shrink-0 items-center gap-1.5 self-start justify-self-end pt-2 text-body font-medium text-muted-foreground transition-colors hover:text-foreground @max-[560px]:col-start-1 @max-[560px]:row-start-3 @max-[560px]:justify-self-start",
            QUIET_LINK
          )}
        >
          View full schedule in Healthie
          <ExternalLink aria-hidden="true" className="size-3.5" />
        </a>
      ) : null}
      <p className="col-start-1 row-start-2 mt-1 text-sm text-muted-foreground">
        {homeSubtitle(header.display, header.todayCount)}
      </p>
      {/* With the Week view on, the switch is a full-width bar under the
          header (the RD Schedule Manager canvas); each trigger's underline
          sits on the bar's hairline. */}
      <TabsList
        variant="underline"
        aria-label="Appointments"
        className={
          week
            ? "col-span-full row-start-3 mt-5 flex justify-start border-b border-border text-muted-strong @max-[560px]:row-start-4 @max-[560px]:mt-4"
            : "col-start-2 row-start-2 mt-1 justify-self-end text-muted-strong @max-[560px]:col-start-1 @max-[560px]:row-start-4 @max-[560px]:mt-4 @max-[560px]:justify-self-start"
        }
      >
        {appointmentViewTabs(showAvailability, week !== null).map((tab) => (
          <TabsTrigger
            key={tab.value}
            ref={tab.value === "past" ? pastTabRef : undefined}
            value={tab.value}
            className={cn(AGENDA_TAB_INK, week && "-mb-px")}
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </div>
  );
}

function UpcomingPanel({
  agenda,
  listSearch,
  showNonDefaultStatuses,
  viewingOther,
  onStatusChange,
  onLoadMore,
  onViewPast,
}: AppointmentAgendaProps & {
  agenda: AppointmentAgendaReady;
  onViewPast: () => void;
}): ReactElement {
  return (
    <div aria-busy={agenda.isFetching}>
      <AppointmentSchedule
        sections={agenda.sections}
        listSearch={listSearch}
        upNextId={agenda.upNext?.appointment.id ?? null}
        endedTodayIds={agenda.endedTodayIds}
        showNonDefaultStatuses={showNonDefaultStatuses}
        viewingOther={viewingOther}
        onStatusChange={onStatusChange}
        onViewPast={onViewPast}
      />

      {agenda.hasMore ? (
        <div className="mt-7 flex justify-center">
          <Button
            variant="utility"
            size="compact"
            shape="pill"
            className={cn("px-5 text-body text-muted-strong", AGENDA_UTILITY_RADIUS)}
            aria-disabled={agenda.isFetchingMore}
            onClick={() => {
              if (agenda.isFetchingMore) return;

              onLoadMore();
            }}
          >
            {agenda.isFetchingMore ? "Loading more…" : "Show more appointments"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The RD's own h1. Second person, viewer only: parsing a first name is
 * acceptable only where the subject can see the guess. A viewer who can name
 * an RD gets the scope selector as their h1 instead, which renders the selected
 * name verbatim — do not converge the two (NO-467).
 */
export function GreetingHeading({
  display,
  userName,
  headingRef,
}: {
  display: { now: string; timezone: string };
  userName: string | undefined;
  headingRef: RefObject<HTMLHeadingElement | null>;
}): ReactElement {
  return (
    <h1
      ref={headingRef}
      tabIndex={-1}
      className={cn(
        "truncate font-sans text-3xl font-bold tracking-tight text-foreground outline-none",
        PAGE_TITLE
      )}
    >
      {greetingHeadline(display.now, display.timezone, userName)}
    </h1>
  );
}

function greetingHeadline(
  now: string,
  timezone: string,
  userName: string | undefined
): string {
  const greeting = timeOfDayGreeting(now, timezone);
  const firstName = userName ? rdFirstName(userName) : null;

  return firstName ? `${greeting}, ${firstName}` : greeting;
}

// No timezone label: times already render in the device clock, so naming the
// zone would be noise (the "agenda as lived" decision, 2026-08-16).
function homeSubtitle(
  display: { now: string; timezone: string },
  todayCount: number | null
): string {
  const parts = [headerDateLabel(display.now, display.timezone)];

  if (todayCount !== null) {
    parts.push(
      todayCount === 0
        ? "No appointments today"
        : `${todayCount} appointment${todayCount === 1 ? "" : "s"} today`
    );
  }

  return parts.join(" · ");
}

function UpNextCard({
  upNext,
  timezone,
  listSearch,
}: {
  upNext: UpNextAppointment;
  timezone: string;
  listSearch: AppointmentListSearch;
}): ReactElement {
  const { appointment, kind } = upNext;
  const eyebrow =
    kind === "now"
      ? "Happening now"
      : kind === "soon"
        ? "Up next · Starting soon"
        : "Up next";

  return (
    <Card variant="hero" className="mt-5 flex flex-wrap items-center gap-5 px-7 py-6">
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "font-sans text-xs font-extrabold uppercase tracking-[0.08em] text-info",
            AGENDA_EYEBROW
          )}
        >
          {eyebrow}
        </p>
        <h2 className="mt-1.5 truncate font-sans text-heading font-bold text-foreground brand:text-heading-sm">
          {appointment.patientName || "Patient"}
        </h2>
        <p className="mt-1 text-body-lg text-muted-foreground">
          {formatAppointmentTimeRange(appointment, timezone)} ·{" "}
          {appointment.appointmentType}
        </p>
      </div>
      <Button
        asChild
        variant="press"
        size="compact"
        shape="pill"
        className={cn("px-4 text-body", AGENDA_PRIMARY_FILL)}
      >
        <Link
          to="/appointments/$appointmentId"
          params={{ appointmentId: appointment.id }}
          search={listSearch}
        >
          View appointment
        </Link>
      </Button>
    </Card>
  );
}
